/**
 * Adresele din Today / Ahead / ITIL / NIS2, din cardurile de dashboard si din
 * orice loc care primeste de la server o adresa scrisa ca in portal
 * (`modul/pagina`).
 *
 * Ecranele Overview mutate raman sub /app/overview/*. Listele de masini
 * Classic (live_devices, unassigned, inactive, discovery) nu se reconstruiesc.
 *
 * Portalul vechi NU mai sta pe server (11 septembrie 2026). O adresa care are
 * ruta in `router.tsx` trebuie sa iasa de aici cu `/app` in fata; fara el,
 * `<a href>` ocoleste basepath-ul routerului si pleaca in gol. Ce n-are ruta
 * ramane scris ca in portal - acolo chiar nu exista nimic, dar macar se vede
 * in adresa CE lipseste.
 */

export const OVERVIEW_PAGES = ['today', 'ahead', 'dashboard', 'domains', 'nis2'] as const
export type OverviewPage = (typeof OVERVIEW_PAGES)[number]

const DEAD_PREFIXES = [
  'overview/live_devices',
  'overview/unassigned',
  'overview/unnasigned',
  'overview/inactive',
  'overview/workstations',
  'overview/servers',
  'overview/mobiles',
  'overview/discovery',
  'overview/network_discovery',
  'overview/remote_connect',
]

/*
 * Paginile care au PLECAT din CodeIgniter, adica exact listele `PAGES` din
 * application/Services/V4/*Cut.php. O adresa de-a lor scrisa ca in portal
 * (`inventoree/printers`) trebuie sa ajunga la ecranul din /app, nu sa iasa
 * din aplicatie: portalul oricum o intoarce cu 302 tot acolo, dar drumul
 * inseamna o incarcare completa a paginii, dus si intors, pentru un ecran
 * care e deja pe loc.
 *
 * Potrivirea pe prefix se face cu `cheie + '/'`, deci `inventoree/device`
 * prinde `inventoree/device/123` dar nu `inventoree/deviceDetails/123`. Aceea
 * e o FILA de fisa: o ducea portalul la `#open=`, iar acum o duce `deviceTab()`
 * de mai jos, fiindca portalul nu mai e.
 *
 * Tablourile de modul (`<modul>/dashboard`) si tot Easeedesk-ul au ruta in
 * /app de pe 9-11 septembrie 2026, deci stau aici - proba din href.test.ts le
 * cere cu `/app`.
 */
export const IN_APP: Record<string, string> = {
  /* OverviewCut */
  'overview/today': '/overview/today',
  'overview/ahead': '/overview/ahead',
  'overview/dashboard': '/overview/dashboard',
  'overview/domains': '/overview/domains',
  'overview/nis2': '/overview/nis2',

  /*
   * Problems, mutat cu tot cu fisa si cu tiparele.
   *
   * Prefixul prinde si `problems/view/12`, care e chiar forma pe care o scrie
   * tabloul ITIL in coloana „Open problems" - forma portalului, cu `view/`,
   * nu `problems/12`. Randul asta pleaca in acelasi commit cu rutele; fara
   * ruta, legatura ar duce in /app intr-un 404.
   */
  problems: '/problems',

  /* InventoreeCut */
  'inventoree/workstations': '/inventoree/workstations',
  'inventoree/servers': '/inventoree/servers',
  'inventoree/inactive': '/inventoree/inactive',
  'inventoree/decommissioned': '/inventoree/decommissioned',
  'inventoree/mobiles': '/inventoree/mobiles',
  'inventoree/unmanaged': '/inventoree/unmanaged',
  'inventoree/network': '/inventoree/network',
  'inventoree/agentless': '/inventoree/agentless',
  'inventoree/printers': '/inventoree/printers',
  'inventoree/reports': '/inventoree/reports',
  'inventoree/software_licenses': '/inventoree/software_licenses',
  'inventoree/unassigned': '/inventoree/unassigned',
  'inventoree/alarms': '/inventoree/alarms',
  'inventoree/live_devices': '/inventoree/live_devices',
  'inventoree/live_agentbased': '/inventoree/live_agentbased',
  'inventoree/inventory_map': '/inventoree/inventory_map',
  'inventoree/endpoint_performance_dashboard': '/inventoree/endpoint_performance_dashboard',
  'inventoree/device': '/inventoree/device',

  /* ProactiviteeCut */
  'proactivitee/alarms': '/proactivitee/alarms',
  'proactivitee/all_devices': '/proactivitee/all_devices',
  'proactivitee/workstations': '/proactivitee/workstations',
  'proactivitee/servers': '/proactivitee/servers',
  'proactivitee/network_devices': '/proactivitee/network_devices',
  'proactivitee/mobiles': '/proactivitee/mobiles',
  'proactivitee/extended_monitoring': '/proactivitee/extended_monitoring',
  'proactivitee/snmp_dashboard': '/proactivitee/snmp_dashboard',
  'proactivitee/snmp_services': '/proactivitee/snmp_services',
  'proactivitee/snmp_host_monitoring': '/proactivitee/snmp_host_monitoring',
  'proactivitee/core_applications': '/proactivitee/core_applications',
  'proactivitee/core_applications_dashboard': '/proactivitee/core_applications_dashboard',
  'proactivitee/applications_dashboard': '/proactivitee/applications_dashboard',
  'proactivitee/device': '/proactivitee/device',

  /* OpteemumCut */
  'opteemum/alarms': '/opteemum/alarms',
  'opteemum/workstations': '/opteemum/workstations',
  'opteemum/servers': '/opteemum/servers',
  'opteemum/mobiles': '/opteemum/mobiles',
  'opteemum/inactive': '/opteemum/inactive',
  'opteemum/live_devices': '/opteemum/live_devices',
  'opteemum/unassigned': '/opteemum/unassigned',
  'opteemum/applications': '/opteemum/applications',
  'opteemum/schedule_management': '/opteemum/schedule_management',
  'opteemum/remote_connect': '/opteemum/remote_connect',
  'opteemum/remote_sessions': '/opteemum/remote_sessions',
  'opteemum/device': '/opteemum/device',

  /* SoftseeCut */
  'softsee/all_devices': '/softsee/all_devices',
  'softsee/workstations': '/softsee/workstations',
  'softsee/servers': '/softsee/servers',
  'softsee/mobiles': '/softsee/mobiles',
  'softsee/applications': '/softsee/applications',
  'softsee/licenses': '/softsee/licenses',
  'softsee/certificates': '/softsee/certificates',
  'softsee/procedures': '/settings/softsee/procedures',
  'softsee/installMonitor': '/softsee/installMonitor',
  'softsee/device': '/softsee/device',

  /* SettingsCut - hub-ul si filele lui stau sub aceeasi radacina */
  'settings/inventoree': '/settings/inventoree',
  'settings/softsee': '/settings/softsee',
  'settings/opteemum': '/settings/opteemum',
  'settings/proactivitee': '/settings/proactivitee',
  'settings/diagnostic_reports': '/settings/diagnostic_reports',

  /*
   * Policies > Agent Autonomy. Doua randuri din Today ("Alarms answered
   * today", "Tickets the AI desk took today") scriu `settings/autonomy`; fara
   * cheia asta ieseau `/settings/autonomy`, fara `/app`, adica in portalul
   * care nu mai e pe server.
   */
  'settings/autonomy': '/settings/autonomy',
  /* Settings > Protectee, cu filele lui (cardul testului de restaurare duce la `restore_tests`) */
  'settings/protectee': '/settings/protectee',
  /* Settings > Securitee > XDR: cardul masinilor fara EDR la firmele NIS2 duce acolo */
  'settings/securitee/xdr': '/settings/securitee/xdr',

  /*
   * Catalogul de servicii, la RADACINA ca in portal.
   *
   * Potrivirea pe prefix duce si `service_catalog/costsDashboard` (butonul
   * "Dashboard Services Costs" din All Reports), si `service_catalog/costs/12`
   * (fisa unui element din CMDB) la ecranele din /app, fara sa fie scrise una
   * cate una.
   */
  service_catalog: '/service_catalog',

  /* Alarmele de flota: aceeasi ruta `$door/alarms` in v4 */
  'securitee/alarms': '/securitee/alarms',

  /* Tablourile de modul: `<modul>DashboardRoute`, 9-10 septembrie 2026 */
  'inventoree/dashboard': '/inventoree/dashboard',
  'softsee/dashboard': '/softsee/dashboard',
  'opteemum/dashboard': '/opteemum/dashboard',
  'proactivitee/dashboard': '/proactivitee/dashboard',
  'easeedesk/dashboard': '/easeedesk/dashboard',
  /* Securitee (11 sept. 2026) si Protectee (12 sept. 2026) in /app; `protectee` gol e
     legatura literei C din NIS2 (`letters.ts`) si duce la tabloul modulului. */
  'securitee/dashboard': '/securitee/dashboard',
  'securitee/all_devices': '/securitee/all_devices',
  'securitee/events': '/securitee/events',
  'securitee/incidents': '/securitee/incidents',
  'securitee/detections': '/securitee/detections',
  'securitee/gravityzone': '/securitee/gravityzone',
  'securitee/ad-accounts': '/securitee/ad-accounts',
  'securitee/local-accounts': '/securitee/local-accounts',
  protectee: '/protectee/dashboard',
  'protectee/dashboard': '/protectee/dashboard',
  'protectee/all_devices': '/protectee/all_devices',
  'printeeng/dashboard': '/printeeng/dashboard',
  'printeeng/all_devices': '/printeeng/all_devices',
  'printeeng/printers': '/printeeng/printers',
  'printeeng/events': '/printeeng/events',
  printeeng: '/printeeng/dashboard',
  'saveenergee/dashboard': '/saveenergee/dashboard',
  'saveenergee/all_devices': '/saveenergee/all_devices',
  'saveenergee/events': '/saveenergee/events',
  saveenergee: '/saveenergee/dashboard',
  'productivitee/dashboard': '/productivitee/dashboard',
  'productivitee/device_productivity': '/productivitee/device_productivity',
  'productivitee/user_productivity': '/productivitee/user_productivity',
  'productivitee/user_productivity_period': '/productivitee/user_productivity_period',
  productivitee: '/productivitee/dashboard',

  /*
   * Easeedesk: fisa tichetului (`/easeedesk/ticket/$id`, cu fila ei si cu
   * capetele din email: approve/deny), listele (`/easeedesk/$list`), tablourile
   * si rapoartele. Prefixul `easeedesk/ticket` prinde si `easeedesk/ticket/12`,
   * si `easeedesk/ticket/approve/<jeton>`; `easeedesk/tickets` e alta cheie
   * (lista), fiindca potrivirea pe prefix cere `/` dupa cheie.
   */
  'easeedesk/ticket': '/easeedesk/ticket',
  'easeedesk/tickets': '/easeedesk/tickets',
  'easeedesk/closed_tickets': '/easeedesk/closed_tickets',
  'easeedesk/tickets_dashboard': '/easeedesk/tickets_dashboard',
  'easeedesk/dashboards': '/easeedesk/dashboards',
  'easeedesk-dashboards': '/easeedesk-dashboards',
  'easeedesk/all_reports': '/easeedesk/all_reports',
  'easeedesk/customers': '/easeedesk/customers',
  'easeedesk/it_solutions': '/easeedesk/it_solutions',
  'easeedesk/it_policies': '/easeedesk/it_policies',
  'easeedesk/oncall': '/easeedesk/oncall',
  /* Aprobarea managerului solicitantului: ce asteapta hotararea omului (clopotelul). */
  'easeedesk/approvals': '/easeedesk/approvals',
  'easeedesk/gps_tracking': '/easeedesk/gps_tracking',
  'easeedesk/my_performance': '/easeedesk/my_performance',
  'easeedesk/my_new_performance': '/easeedesk/my_new_performance',
  'easeedesk/customer_ticket_details': '/easeedesk/customer_ticket_details',
  'easeedesk/reject_planned_ticket': '/easeedesk/reject_planned_ticket',
  'easeedesk/reopen_resolved_ticket': '/easeedesk/reopen_resolved_ticket',
  'easeedesk/ticket_feedback': '/easeedesk/ticket_feedback',

  /* Registrul CMDB, cu fisa (`cmdb/view/3`), sanatatea si liniile de baza */
  cmdb: '/cmdb',
  /* ADM intreg: tablou, harta, impact, rulari, erori, programari, schimbari */
  adm: '/adm',
  maintenance: '/maintenance',
  dashboard: '/dashboard',
  self_service: '/self_service',
  service_shop: '/service_shop',
  change_log: '/change_log',
  bug_reports: '/bug_reports',
  feature_requests: '/feature_requests',
  'services_providers/service_providers': '/services_providers/service_providers',
  'remote_connect/rustdesk_launch': '/remote_connect/rustdesk_launch',
  'monitor/wallboard': '/monitor/wallboard',
}

/*
 * FILELE FISEI, scrise ca in portal: `inventoree/deviceDetails/123`,
 * `opteemum/deviceHddDetails/123`, `proactivitee/deviceSummary/7`.
 *
 * In portal, `_remap` le ducea la fisa cu `#open=<fila>`. In v4 fisa are o
 * singura adresa pe modul - `<modul>/device/{id}` - iar fila se alege tot din
 * `#open=` (vezi `startingHref` din DeviceWorkspace). Lista e cea a optiunilor
 * din meniul fisei (`DbalDeviceTabsSource`), plus cele pe care le scrie
 * `CoverageEngine` pentru Ahead. O fila necunoscuta cade pe Overview - tot
 * fisa, nu un 404.
 *
 * Modulul fisei e cel din adresa cand are fisa in v4; `cmdb/deviceView/`,
 * `overview/agentSettings/` si `changes/deviceTodo/` n-au, si se deschid in
 * fisa din Inventoree.
 */
const DEVICE_MODULES = ['inventoree', 'opteemum', 'proactivitee', 'softsee']
const DEVICE_TAB =
  /^([a-z_]+)\/(device[A-Za-z]+|performanceSummary|performanceStatus|applicationManagement|cmd|commandPrompt|currentSessions|networkStatus|osPatchManagement|osUpdates|processLoad|remoteActions|remoteEventViewer|runningApplications|startupApps|startupServices|configRelay|coreServicesMonitoring|extendedMonitoring|externalMonitoring|hardwareMonitoring|neighborWatch|installApplications|uninstallApplications|updateApplications|usedApplications|agentSettings)\/(\d+)$/

function deviceTab(bare: string): string | null {
  const m = DEVICE_TAB.exec(bare)
  if (!m) return null
  const [, module, tab, id] = m
  const from = DEVICE_MODULES.includes(module) ? module : 'inventoree'
  return `/app/${from}/device/${id}#open=${tab}`
}

export function bareHref(url: string): string {
  return url.replace(/^\//, '').split('?')[0] ?? ''
}

export function isDeadHref(url: string): boolean {
  const bare = bareHref(url)
  return DEAD_PREFIXES.some((prefix) => bare === prefix || bare.startsWith(prefix + '/'))
}

export function isEaseedeskHref(url: string): boolean {
  return bareHref(url).startsWith('easeedesk')
}

/*
 * Adrese de catalog FARA ecran in v4, duse la ecranul care raspunde la aceeasi
 * intrebare. Numai potrivire intreaga. Perechea e `SAME_QUESTION` din
 * v4/api/src/Legacy/MovedPages.php - cardurile de tablou trec pe acolo, iar
 * sfatul de sub carduri si clopotelul trec pe aici; `dashboardHrefs.test.ts`
 * pica daca cele doua liste se despart.
 *
 * `protectee/backup_summary` (cele patru carduri Protectee) n-a fost pagina
 * nici in portal - e numele unui model. Raspunsul e lista de masini Protectee,
 * cu coloana "Backup Sessions" si fila de backup a fiecarei masini.
 * `discovery` e randul "Found on the network" din Today.
 */
export const SAME_QUESTION: Record<string, string> = {
  discovery: '/inventoree/live_devices',
  'protectee/backup_summary': '/protectee/all_devices',
}

/*
 * PREFIXUL SE POTRIVESTE NUMAI CAND CHEIA E CHIAR CALEA.
 *
 * `adm` -> `/adm` poate prinde si `adm/changes/software`: coada se lipeste la
 * aceeasi cale si ajunge pe ruta ei. Dar `protectee` -> `/protectee/dashboard`
 * (si `printeeng`, `saveenergee`, `productivitee`, `softsee/procedures`) e un
 * ALIAS, nu o radacina: pe prefix, `protectee/backup_summary` iesea
 * `/app/protectee/dashboard/backup_summary` - o pagina "not found", desenata
 * pe tabloul central sub fiecare card Protectee (18 septembrie 2026). Un alias
 * raspunde numai la adresa lui intreaga.
 */
function isRoot(key: string): boolean {
  return IN_APP[key] === '/' + key
}

/** Adresa din pagina: /app/... daca ecranul e in v4, altfel portalul. Null = moarta, nu se deseneaza. */
export function hrefOf(url: string | null | undefined): string | null {
  if (!url) return null
  if (isDeadHref(url)) return null
  const bare = bareHref(url)
  const same = SAME_QUESTION[bare]
  if (same) return '/app' + same
  const exact = IN_APP[bare]
  if (exact) return '/app' + exact
  /* Fila de fisa INAINTEA prefixului: `cmdb/deviceView/7` e o fila, nu o
     pagina de sub `cmdb`. */
  const tab = deviceTab(bare)
  if (tab !== null) return tab
  const prefix = Object.keys(IN_APP).find((key) => isRoot(key) && bare.startsWith(key + '/'))
  if (prefix) {
    return '/app' + IN_APP[prefix] + bare.slice(prefix.length)
  }
  return '/' + bare
}
